import { motion } from "framer-motion";
import { ShieldCheck, Users, ClipboardCheck, FileText, BadgeCheck } from "lucide-react";

const EASE = [0.76, 0, 0.24, 1];

const points = [
    { icon: Users, title: "Trained teams", copy: "Held to defined standards on every visit." },
    { icon: ClipboardCheck, title: "Supervisor checks", copy: "Documented checks and daily sign-off sheets on every site." },
    { icon: ShieldCheck, title: "Food-safe materials", copy: "Cleaning materials backed by manufacturer documentation." },
    { icon: FileText, title: "Monthly quality reporting", copy: "With a clear process for resolving any issue." },
    { icon: BadgeCheck, title: "FMCG-grade experience", copy: "Meeting hygiene standards and passing regular client audits." },
];

export default function About() {
    return (
        <section id="about" className="py-24 md:py-32 px-6 md:px-12 max-w-7xl mx-auto" data-testid="about-section">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
                <motion.div
                    initial={{ opacity: 0, scale: 0.96 }}
                    whileInView={{ opacity: 1, scale: 1 }}
                    viewport={{ once: true, margin: "-80px" }}
                    transition={{ duration: 1, ease: EASE }}
                    className="lg:col-span-5 spotlight-frame"
                >
                    <div className="overflow-hidden rounded-[1.1rem] h-[420px] md:h-[540px]">
                        <img
                            src="/team.jpg"
                            alt="The Zami Concept cleaning team in uniform"
                            className="w-full h-full object-cover"
                        />
                    </div>
                </motion.div>

                <div className="lg:col-span-7">
                    <motion.div
                        initial={{ opacity: 0, y: 32 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, margin: "-80px" }}
                        transition={{ duration: 0.9, ease: EASE }}
                    >
                        <p className="text-xs font-bold tracking-[0.25em] uppercase text-fern mb-4">Why Zami</p>
                        <h2 className="font-serif font-medium tracking-tight leading-tight text-3xl sm:text-4xl lg:text-5xl text-forest mb-6 text-balance">
                            About The Zami Concept
                        </h2>
                        <p className="text-base font-light leading-relaxed tracking-wide text-fern max-w-xl mb-12">
                            The Zami Concept provides reliable, efficient commercial cleaning that businesses
                            across Lagos and beyond can depend on. A clean, well-maintained workplace becomes
                            one less thing you have to manage. That reliability runs through everything we do,
                            from the standards our teams hold on site to the way we communicate with you. We
                            clean offices, retail and food-service outlets, and industrial and manufacturing
                            facilities, keeping your environment clean, hygienic, and presentable with minimal
                            disruption to your operations.
                        </p>
                    </motion.div>

                    <div className="flex flex-col gap-8">
                        {points.map((p, i) => (
                            <motion.div
                                key={p.title}
                                data-testid={`about-point-${i}`}
                                initial={{ opacity: 0, y: 24 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true, margin: "-40px" }}
                                transition={{ duration: 0.7, ease: EASE, delay: i * 0.1 }}
                                className="flex gap-5 items-start"
                            >
                                <div className="shrink-0 w-11 h-11 rounded-full bg-sage flex items-center justify-center">
                                    <p.icon className="w-5 h-5 text-forest" strokeWidth={1.5} />
                                </div>
                                <div>
                                    <h3 className="font-serif text-xl font-medium tracking-tight text-forest mb-1">{p.title}</h3>
                                    <p className="text-sm font-light leading-relaxed text-fern max-w-md">{p.copy}</p>
                                </div>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </div>
        </section>
    );
}
